/* eslint-disable */

/**
 * CustomExternalLink - Editor.js inline tool for adding external URLs
 *
 * Replaces the built-in link tool with a wider input and OK/Cancel buttons.
 * Based on: https://github.com/codex-team/editor.js/blob/master/src/components/inline-tools/inline-tool-link.ts
 */

require('./custom-external-link.css').toString();

export default class CustomExternalLink {

  static get isInline() {
    return true;
  }

  get shortcut() {
    return 'CMD+K';
  }

  static get sanitize() {
    return {
      a: {
        href: true,
        target: true,
        rel: true
      }
    };
  }

  static get title() {
    return 'Link';
  }

  get state() {
    return this._state;
  }

  set state(state) {
    this._state = state;
    if (this.button && this.api && this.api.styles) {
      this.button.classList.toggle(this.api.styles.inlineToolButtonActive, state);
    }
  }

  constructor({ api }) {
    this.api = api;
    this.button = null;
    this._state = false;
    this.range = null;
    this.inputOpened = false;

    this.nodes = {
      wrapper: null,
      input: null
    };

    this.tag = 'A';
  }

  render() {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.innerHTML = '<svg width="15" height="14" viewBox="0 0 15 14" xmlns="http://www.w3.org/2000/svg"><path d="M13.073 1.926c-1.239-1.24-3.25-1.24-4.489 0L6.451 4.06a3.17 3.17 0 0 0-.354 4.104l.59-.59a2.153 2.153 0 0 1 .133-2.926l2.133-2.133a2.174 2.174 0 0 1 3.075 0 2.174 2.174 0 0 1 0 3.075l-1.978 1.979a3.6 3.6 0 0 1-.267 1.468l2.69-2.691c1.24-1.24 1.24-3.25 0-4.49zM4.927 12.074c1.24 1.24 3.25 1.24 4.49 0l2.132-2.133a3.17 3.17 0 0 0 .354-4.104l-.59.59a2.153 2.153 0 0 1-.133 2.926l-2.133 2.133a2.174 2.174 0 0 1-3.075 0 2.174 2.174 0 0 1 0-3.075l1.978-1.979a3.6 3.6 0 0 1 .267-1.468L5.527 7.654c-1.24 1.24-1.24 3.25 0 4.49z"/></svg>';

    if (this.api && this.api.styles) {
      this.button.classList.add(this.api.styles.inlineToolButton);
    }

    return this.button;
  }

  renderActions() {
    this.nodes.wrapper = document.createElement('div');
    this.nodes.wrapper.classList.add('cel-wrapper');

    // Label
    var label = document.createElement('span');
    label.classList.add('cel-label');
    label.textContent = 'Enter or paste a link';

    // Input
    this.nodes.input = document.createElement('input');
    this.nodes.input.classList.add('cel-input');
    this.nodes.input.placeholder = 'https://example.com';
    this.nodes.input.type = 'url';

    // Enter key → apply link
    this.nodes.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        this.applyLink();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.closeActions();
        this.api.inlineToolbar.close();
      }
    });

    // Stop input events from bubbling to Editor.js (prevents block deletion etc.)
    this.nodes.input.addEventListener('keyup', function(e) { e.stopPropagation(); });

    // Buttons container
    var btnRow = document.createElement('div');
    btnRow.classList.add('cel-buttons');

    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.classList.add('cel-btn', 'cel-btn--cancel');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.closeActions();
      this.api.inlineToolbar.close();
    });

    var okBtn = document.createElement('button');
    okBtn.type = 'button';
    okBtn.classList.add('cel-btn', 'cel-btn--ok');
    okBtn.textContent = 'OK';
    okBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.applyLink();
    });

    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(okBtn);

    this.nodes.wrapper.appendChild(label);
    this.nodes.wrapper.appendChild(this.nodes.input);
    this.nodes.wrapper.appendChild(btnRow);

    return this.nodes.wrapper;
  }

  surround(range) {
    if (!range) return;

    // If already a link, unwrap it (toggle behavior)
    var existingLink = this.api.selection.findParentTag(this.tag);
    if (existingLink) {
      this.unwrap(existingLink);
      this.closeActions();
      return;
    }

    this.range = range;
    this.toggleActions();
  }

  toggleActions() {
    if (!this.inputOpened) {
      this.openActions();
    } else {
      this.closeActions();
    }
  }

  openActions() {
    this.nodes.wrapper.classList.remove('cel-wrapper');
    this.nodes.wrapper.classList.add('cel-wrapper--showed');

    // If editing an existing link, pre-fill the input
    var existingLink = this.api.selection.findParentTag(this.tag);
    if (existingLink) {
      this.nodes.input.value = existingLink.getAttribute('href') || '';
    } else {
      this.nodes.input.value = '';
    }

    this.inputOpened = true;

    // Focus the input after a small delay (lets the popup render)
    setTimeout(() => {
      this.nodes.input.focus();
    }, 50);
  }

  closeActions() {
    this.nodes.wrapper.classList.remove('cel-wrapper--showed');
    this.nodes.wrapper.classList.add('cel-wrapper');
    this.nodes.input.value = '';
    this.inputOpened = false;
  }

  applyLink() {
    var url = this.nodes.input.value.trim();

    if (!url) {
      // If no URL entered, just close
      this.closeActions();
      this.api.inlineToolbar.close();
      return;
    }

    // Auto-prepend https:// if user typed a bare domain
    if (url && !url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('/') && !url.startsWith('{{')) {
      url = 'https://' + url;
    }

    this.wrap(url);
    this.closeActions();
    this.api.inlineToolbar.close();
  }

  wrap(url) {
    if (!this.range) return;

    // Check if we're editing an existing link
    var existingLink = this.api.selection.findParentTag(this.tag);
    if (existingLink) {
      // Update existing link's href
      existingLink.setAttribute('href', url);
    } else {
      // Create a new link around the selected text
      var selectedText = this.range.extractContents();
      var link = document.createElement('a');
      link.setAttribute('href', url);
      link.appendChild(selectedText);
      this.range.insertNode(link);
    }
  }

  unwrap(link) {
    // Replace the <a> element with its text content
    var text = document.createDocumentFragment();
    while (link.firstChild) {
      text.appendChild(link.firstChild);
    }
    link.parentNode.replaceChild(text, link);
  }

  checkState(selection) {
    if (!selection || !selection.anchorNode) {
      this.state = false;
      return;
    }

    var anchor = selection.anchorNode;
    var el = anchor instanceof Element ? anchor : anchor.parentElement;
    if (!el) {
      this.state = false;
      return;
    }

    this.state = !!el.closest('a');
  }

  clear() {
    this.closeActions();
  }
}
